/**
 * إتاحات المقيّمات — الأيام والساعات اللي كل مقيّمة فاضية فيها.
 *
 * 🔴 **كوليكشن `availability` كان موجود وفاضي من يوم ما اتعمل.** شاشة
 *    الحجز كانت مكتوب فيها حرفيًا «إتاحات المقيّمين لسه مش مسجّلة — الموعد
 *    بيتكتب بإيدك»، فموظفة دعم الطلاب كانت بتتفق على ساعة مع ولي الأمر
 *    **من غير ما تعرف مين فاضي أصلًا**، والتعارض بيتكشف بعد الحجز.
 *
 * ⛔ **القاعدة اللي بتحكم الملف ده: ممنوع نخترع إتاحة.** المقيّمة اللي
 *    ما سجّلتش بتبان «لسه ما سجّلتش إتاحتها» — مش شبكة فاضية تتقرا
 *    «مش متاحة»، ومش شبكة كاملة تتقرا «متاحة طول الوقت».
 */
import { api } from "./client";

/** ⛔ رد بشكل غير متوقع = خطأ صائح، مش قيم افتراضية بتخلّي الشاشة تكدب. */
function fail(path: string, why: string): never {
  console.error(`[availability] ${path}: ${why}`);
  throw new Error(why);
}

export type Slot = {
  day_of_week: number;
  start_local: string;
  end_local: string;
  day_label_ar?: string;
};

export type Availability = {
  owner_ref: string;
  has_availability: boolean;
  tz: string;
  slots: Slot[];
  note_ar: string | null;
  /** «لسه ما سجّلتش إتاحتها» · «سجّلت إنها مش متاحة» · «إتاحة مسجّلة» */
  state_ar: string;
  version: number;
};

export type EvaluatorBrief = {
  id: string;
  name_ar: string;
  languages: string[];
  subjects: string[];
};

export type FreeSlot = {
  start_at: string;
  end_at: string;
  duration_minutes: number;
  day_label_ar: string;
  date_local: string;
  start_local: string;
  end_local: string;
  tz: string;
};

export type SlotMapRow = {
  evaluator: EvaluatorBrief;
  availability: Availability;
  booked_count: number;
  free_slots: FreeSlot[];
};

export type SlotMap = {
  rows: SlotMapRow[];
  /** المقيّمات اللي لسه ما سجّلوش إتاحة — بيتقال صريح مش بيتخبّى */
  withoutAvailability: number;
  /** عدد المقيّمات في السيستم كله — ⛔ من غيره الشاشة بتقول «مفيش متاح»
   *  وهي المفروض تقول «مفيش مقيّمين أصلًا». الفرق بين «الفريق مشغول»
   *  و«الفريق مش موجود» بيغيّر اللي المستخدم هيعمله تمامًا. */
  evaluatorsTotal: number;
  academyTz: string;
  dayLabels: Record<string, string>;
  noteAr: string | null;
};

function asAvailability(raw: unknown, path: string): Availability {
  const d = (raw ?? null) as Record<string, unknown> | null;
  if (!d || typeof d !== "object") fail(path, "الإتاحة رجعت بشكل مش مفهوم");
  return {
    owner_ref: String(d!["owner_ref"] ?? ""),
    has_availability: d!["has_availability"] === true,
    tz: String(d!["tz"] ?? "Africa/Cairo"),
    slots: Array.isArray(d!["slots"]) ? (d!["slots"] as Slot[]) : [],
    note_ar: (d!["note_ar"] as string | null) ?? null,
    state_ar: String(d!["state_ar"] ?? ""),
    version: Number(d!["version"] ?? 0),
  };
}

export async function fetchMyAvailability(): Promise<Availability> {
  const path = "/evaluation/availability/me";
  const res = await api.get<unknown>(path);
  return asAvailability(res.data, path);
}

export async function saveMyAvailability(
  slots: Slot[],
  noteAr?: string | null,
): Promise<{ availability: Availability; toastAr: string }> {
  const path = "/evaluation/availability/me";
  const res = await api.put<unknown>(path, {
    slots: slots.map((s) => ({
      day_of_week: s.day_of_week,
      start_local: s.start_local,
      end_local: s.end_local,
    })),
    ...(noteAr?.trim() ? { note_ar: noteAr.trim() } : {}),
  });
  const d = (res.data ?? {}) as Record<string, unknown>;
  return {
    availability: asAvailability(res.data, path),
    toastAr: String(d["toast_ar"] ?? "الإتاحة اتحفظت"),
  };
}

/** الخريطة الشاملة: مين فاضي · إمتى · بيقيّم إيه — ناقص المحجوز بالفعل. */
export async function fetchSlotMap(input: {
  from: string;
  to: string;
  subject?: string;
  language?: string;
  slotMinutes?: number;
}): Promise<SlotMap> {
  const path = "/evaluation/slot-map";
  const res = await api.get<unknown>(path, {
    query: {
      from: input.from,
      to: input.to,
      ...(input.subject ? { subject: input.subject } : {}),
      ...(input.language ? { language: input.language } : {}),
      ...(input.slotMinutes ? { slot_minutes: input.slotMinutes } : {}),
    },
  });
  const rows = Array.isArray(res.data) ? (res.data as SlotMapRow[]) : [];
  const meta = (res.meta ?? {}) as Record<string, unknown>;
  return {
    rows,
    withoutAvailability: Number(meta["without_availability"] ?? 0),
    evaluatorsTotal: Number(meta["evaluators_total"] ?? 0),
    academyTz: String(meta["academy_tz"] ?? "Africa/Cairo"),
    dayLabels: (meta["day_labels_ar"] ?? {}) as Record<string, string>,
    noteAr: (meta["note_ar"] as string | null) ?? null,
  };
}

/** أيام الأسبوع — ⛔ الترتيب من الخادم (الاثنين 0 … الأحد 6). */
export const WEEK_DAYS: readonly { value: number; label: string }[] = [
  { value: 6, label: "الأحد" },
  { value: 0, label: "الاثنين" },
  { value: 1, label: "الثلاثاء" },
  { value: 2, label: "الأربعاء" },
  { value: 3, label: "الخميس" },
  { value: 4, label: "الجمعة" },
  { value: 5, label: "السبت" },
];
